import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { parseDateKey } from './dateFormat';

interface Log {
    id: number;
    action: string;
    description?: string;
    details?: string;
    created_at: string;
    user?: {
        name: string;
        email?: string;
    };
    meta_data?: any;
    metadata?: any;
}

interface Assignment {
    id: number;
    user_id: number;
    assigned_by: number;
    assigned_at: string;
    comment: string;
    user: {
        name: string;
    };
    assigner: {
        name: string;
    };
}

interface Version {
    id: number;
    version_number: number;
    file_name: string;
    created_at: string;
    note?: string;
    is_current: boolean;
    created_by?: any;
    creator?: {
        name: string;
    };
}

interface ReviewComment {
    id: number;
    content: string;
    created_at: string;
    user?: {
        name: string;
        email?: string;
    };
    created_by?: {
        name: string;
        email?: string;
    };
    is_resolved?: boolean;
}

type HistoryItem =
    | { type: 'log', data: Log }
    | { type: 'assignment', data: Assignment }
    | { type: 'version', data: Version }
    | { type: 'review_comment', data: ReviewComment };

interface TaskInfo {
    title: string;
    description?: string;
    status?: string;
    created_at?: string;
    creator?: {
        name: string;
        email?: string;
    };
    template?: {
        name: string;
    };
    agreement_no?: string;
    category?: string;
    agreement_type?: string;
    company?: string;
    template_type?: string;
    updated_at?: string;
    agreement_value?: string;
    period?: string;
    payment_method?: string;
    guarantee?: string;
    penalty_compensation?: string;
}

type RGB = [number, number, number];

const INDIGO: RGB = [79, 70, 229];
const GRAY_TEXT: RGB = [107, 114, 128];
const DARK_TEXT: RGB = [17, 24, 39];
const CARD_BG: RGB = [243, 244, 246];

const TZ = 'Asia/Jakarta';

const fmtDateTime = (value?: string): string => {
    const d = parseDateKey(value);
    if (!d || isNaN(d.getTime())) return '-';
    const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ });
    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ });
    return `${date}, ${time}`;
};

const fmtCell = (value?: string): string => {
    const d = parseDateKey(value);
    if (!d || isNaN(d.getTime())) return '-';
    const date = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: TZ });
    const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: TZ });
    return `${date}\n${time}`;
};

// Action label + color per category
const ACTION_LABELS: Record<string, { label: string; color: RGB }> = {
    CREATE_TASK: { label: 'Task Created', color: [79, 70, 229] },
    UPDATE_TASK: { label: 'Task Updated', color: [79, 70, 229] },
    DELETE_TASK: { label: 'Task Deleted', color: [185, 28, 28] },
    MOVE_TASK_STATUS: { label: 'Status Changed', color: [4, 120, 87] },
    UPDATE_STATUS: { label: 'Status Changed', color: [4, 120, 87] },
    CREATE_COMMENT: { label: 'Comment', color: [29, 78, 216] },
    UPDATE_COMMENT: { label: 'Comment Edited', color: [29, 78, 216] },
    DELETE_COMMENT: { label: 'Comment Deleted', color: [185, 28, 28] },
    CREATE_REVIEW_COMMENT: { label: 'Review Comment', color: [194, 65, 12] },
    REPLY_REVIEW_COMMENT: { label: 'Review Reply', color: [194, 65, 12] },
    UPDATE_REVIEW_COMMENT: { label: 'Review Edited', color: [194, 65, 12] },
    DELETE_REVIEW_COMMENT: { label: 'Review Deleted', color: [185, 28, 28] },
    FLAG_REVIEW_COMMENT_AS_DONE: { label: 'Review Resolved', color: [4, 120, 87] },
    UNFLAG_REVIEW_COMMENT_AS_DONE: { label: 'Review Reopened', color: [194, 65, 12] },
    CREATE_TASK_ASSIGNMENT: { label: 'Assignment', color: [124, 58, 237] },
    UPDATE_TASK_ASSIGNMENT: { label: 'Assignment Updated', color: [124, 58, 237] },
    DELETE_TASK_ASSIGNMENT: { label: 'Assignment Removed', color: [185, 28, 28] },
    TASK_ASSIGNMENT: { label: 'Assignment', color: [124, 58, 237] },
    DOCUMENT_VERSION: { label: 'Document Version', color: [13, 148, 136] },
    REVIEW_COMMENT: { label: 'Review Comment', color: [194, 65, 12] },
};

const titleCase = (s: string) =>
    s.toLowerCase().split('_').filter(Boolean).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const getActionStyle = (action: string) =>
    ACTION_LABELS[action.toUpperCase()] || { label: titleCase(action), color: GRAY_TEXT };

const FIELD_LABELS: Record<string, string> = {
    title: 'Title',
    description: 'Description',
    pid: 'PID',
    template_type: 'Template Type',
    agreement_value: 'Agreement Value',
    payment_method: 'Payment Method',
    period: 'Period',
    guarantee: 'Guarantee',
    penalty_compensation: 'Penalty / Compensation',
    category_id: 'Category',
    agreement_type_id: 'Agreement Type',
    company_id: 'Company',
    template_id: 'Template',
    variables: 'Variables',
    supporting_documents: 'Supporting Documents',
    is_active: 'Active Status',
};

const mentionText = (meta: any): string =>
    Array.isArray(meta?.mentions) && meta.mentions.length > 0
        ? `Mentioned: ${meta.mentions.map((m: any) => '@' + m.name).join(', ')}`
        : '';

const buildLogDetails = (log: Log): string => {
    const meta = log.meta_data || log.metadata || {};
    const action = (log.action || '').toUpperCase();
    const parts: string[] = [];

    if (meta.old_status_name && meta.new_status_name) {
        parts.push(`${meta.old_status_name}  >>  ${meta.new_status_name}`);
        return parts.join('\n');
    }

    if (action === 'UPDATE_TASK') {
        const fields: string[] = Array.isArray(meta.changed_fields) ? meta.changed_fields : [];
        parts.push(fields.length > 0
            ? `Updated: ${fields.map(f => FIELD_LABELS[f] || titleCase(f)).join(', ')}`
            : 'Task details updated');
    } else if (action === 'CREATE_TASK') {
        parts.push('Task created');
    } else if (meta.content || meta.comment || meta.assignee_name) {
        if (action.includes('ASSIGNMENT')) {
            parts.push(`Assignee: ${meta.assignee_name || '-'}`);
        } else if (action === 'DELETE_COMMENT' || action === 'DELETE_REVIEW_COMMENT') {
            parts.push('Removed comment');
        } else if (meta.selected_text) {
            parts.push(`On text: “${meta.selected_text}”`);
        }
    } else {
        parts.push(log.description || log.details || '-');
    }

    const text = meta.content || meta.comment;
    if (text) parts.push(`“${text}”`);
    const mentions = mentionText(meta);
    if (mentions) parts.push(mentions);

    return parts.join('\n');
};

export const generateHistoryReportPdf = (items: HistoryItem[], taskInfo: TaskInfo) => {
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 14;
    const contentW = pageW - margin * 2;

    // ---- Header banner ----
    doc.setFillColor(...INDIGO);
    doc.rect(0, 0, pageW, 32, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('Activity Report', margin, 14);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const subtitle = `${taskInfo.title || '-'}${taskInfo.agreement_no ? `  |  ${taskInfo.agreement_no}` : ''}`;
    doc.text(doc.splitTextToSize(subtitle, contentW)[0], margin, 23);

    let yPos = 42;

    // ---- Detail Task (2-column cards) ----
    doc.setTextColor(...DARK_TEXT);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Detail Task', margin, yPos);
    yPos += 5;

    // Status badge next to heading
    if (taskInfo.status) {
        doc.setFontSize(8);
        const w = doc.getTextWidth(taskInfo.status.toUpperCase()) + 6;
        doc.setFillColor(224, 231, 255);
        doc.roundedRect(pageW - margin - w, yPos - 9.5, w, 6, 3, 3, 'F');
        doc.setTextColor(...INDIGO);
        doc.text(taskInfo.status.toUpperCase(), pageW - margin - w + 3, yPos - 5.3);
    }

    const fields: { label: string; value?: string; full?: boolean }[] = [
        { label: 'Category', value: taskInfo.category },
        { label: 'Agreement Type', value: taskInfo.agreement_type },
        { label: 'Company', value: taskInfo.company },
        { label: 'Template', value: taskInfo.template?.name },
        { label: 'Template Type', value: taskInfo.template_type },
        { label: 'Created By', value: taskInfo.creator?.name ? `${taskInfo.creator.name}${taskInfo.creator.email ? ` (${taskInfo.creator.email})` : ''}` : undefined },
        { label: 'Created', value: taskInfo.created_at ? fmtDateTime(taskInfo.created_at) : undefined },
        { label: 'Updated', value: taskInfo.updated_at ? fmtDateTime(taskInfo.updated_at) : undefined },
        { label: 'Agreement Value', value: taskInfo.agreement_value },
        { label: 'Period', value: taskInfo.period },
        { label: 'Payment Method', value: taskInfo.payment_method },
        { label: 'Guarantee', value: taskInfo.guarantee },
        { label: 'Penalty / Compensation', value: taskInfo.penalty_compensation },
        { label: 'Description', value: taskInfo.description, full: true },
    ];

    const gap = 4;
    const colW = (contentW - gap) / 2;
    const pad = 3;

    const drawCard = (x: number, y: number, w: number, h: number, label: string, lines: string[]) => {
        doc.setFillColor(...CARD_BG);
        doc.roundedRect(x, y, w, h, 2, 2, 'F');
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(7.5);
        doc.setTextColor(...GRAY_TEXT);
        doc.text(label, x + pad, y + 5);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.setTextColor(...DARK_TEXT);
        doc.text(lines, x + pad, y + 10.5);
    };

    const cardHeight = (lines: string[]) => 13 + (lines.length - 1) * 4;
    const prepare = (f: { value?: string; full?: boolean }) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        const w = (f.full ? contentW : colW) - pad * 2;
        const lines: string[] = doc.splitTextToSize(f.value && f.value.trim() ? f.value : '-', w);
        return lines.slice(0, f.full ? 12 : 6);
    };

    yPos += 2;
    const regular = fields.filter(f => !f.full && (f.value || ['Category', 'Agreement Type', 'Template', 'Created By'].includes(f.label)));
    const full = fields.filter(f => f.full && f.value);

    for (let i = 0; i < regular.length; i += 2) {
        const a = regular[i];
        const b = regular[i + 1];
        const la = prepare(a);
        const lb = b ? prepare(b) : [];
        const h = Math.max(cardHeight(la), b ? cardHeight(lb) : 0);
        if (yPos + h > pageH - 20) {
            doc.addPage();
            yPos = 20;
        }
        drawCard(margin, yPos, colW, h, a.label, la);
        if (b) drawCard(margin + colW + gap, yPos, colW, h, b.label, lb);
        yPos += h + gap;
    }
    for (const f of full) {
        const lines = prepare(f);
        const h = cardHeight(lines);
        if (yPos + h > pageH - 20) {
            doc.addPage();
            yPos = 20;
        }
        drawCard(margin, yPos, contentW, h, f.label, lines);
        yPos += h + gap;
    }

    // ---- Activity History ----
    yPos += 4;
    if (yPos > pageH - 50) {
        doc.addPage();
        yPos = 20;
    }
    doc.setTextColor(...DARK_TEXT);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Activity History', margin, yPos);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...GRAY_TEXT);
    doc.text(`${items.length} activities  |  Newest first`, pageW - margin, yPos, { align: 'right' });
    yPos += 4;

    const tableData = items.map(item => {
        let created = '';
        let user = '';
        let actionKey = '';
        let details = '';

        if (item.type === 'log') {
            const log = item.data;
            created = log.created_at;
            const meta = log.meta_data || log.metadata || {};
            user = log.user?.name || meta.user_name || 'System';
            actionKey = log.action || '';
            details = buildLogDetails(log);
        } else if (item.type === 'assignment') {
            const a = item.data;
            created = a.assigned_at;
            user = a.assigner?.name || 'Unknown';
            actionKey = 'TASK_ASSIGNMENT';
            details = `Assigned to ${a.user?.name || 'Unknown'}${a.comment ? `\n“${a.comment}”` : ''}`;
        } else if (item.type === 'version') {
            const v = item.data;
            created = v.created_at;
            user = v.created_by?.name || v.creator?.name || 'Unknown';
            actionKey = 'DOCUMENT_VERSION';
            details = `v${v.version_number} - ${v.file_name}${v.is_current ? ' (Current)' : ''}${v.note ? `\nNote: ${v.note}` : ''}`;
        } else {
            const c = item.data;
            created = c.created_at;
            user = (c.user || c.created_by)?.name || 'Unknown';
            actionKey = 'REVIEW_COMMENT';
            details = `“${c.content}”${c.is_resolved ? '\n[Resolved]' : ''}`;
        }

        return { cells: [fmtCell(created), user, getActionStyle(actionKey).label, details], color: getActionStyle(actionKey).color };
    });

    autoTable(doc, {
        head: [['Date & Time', 'User', 'Activity', 'Details']],
        body: tableData.map(r => r.cells),
        startY: yPos,
        margin: { left: margin, right: margin, bottom: 18 },
        rowPageBreak: 'avoid',
        showHead: 'everyPage',
        styles: {
            fontSize: 8,
            cellPadding: 3,
            valign: 'top',
            textColor: DARK_TEXT,
            lineColor: [229, 231, 235],
            lineWidth: 0.1,
        },
        headStyles: {
            fillColor: INDIGO,
            textColor: 255,
            fontStyle: 'bold',
        },
        alternateRowStyles: {
            fillColor: [249, 250, 251],
        },
        columnStyles: {
            0: { cellWidth: 26, textColor: GRAY_TEXT },
            1: { cellWidth: 32 },
            2: { cellWidth: 32, fontStyle: 'bold' },
            3: { cellWidth: 'auto' },
        },
        didParseCell: (data: any) => {
            if (data.section !== 'body') return;
            if (data.column.index === 2) {
                data.cell.styles.textColor = tableData[data.row.index].color;
            }
        },
    });

    // ---- Footer on every page ----
    const total = doc.getNumberOfPages();
    const generated = `Generated ${fmtDateTime(new Date().toISOString())}`;
    for (let i = 1; i <= total; i++) {
        doc.setPage(i);
        doc.setDrawColor(229, 231, 235);
        doc.setLineWidth(0.3);
        doc.line(margin, pageH - 12, pageW - margin, pageH - 12);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...GRAY_TEXT);
        doc.text(generated, margin, pageH - 7);
        doc.text(`Page ${i} of ${total}`, pageW - margin, pageH - 7, { align: 'right' });
    }

    const fileName = `Activity_Report_${taskInfo.title.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
};
